import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, query, onSnapshot, deleteDoc, doc, getDoc } from 'firebase/firestore';
import PostCard from '../components/PostCard';
import NotesCarousel from '../components/NotesCarousel';
import { Users } from 'lucide-react';

const MOOD_FILTERS = [
    { label: 'All', value: 'all' },
    { label: 'Friends', value: 'friends', icon: <Users className="w-3.5 h-3.5" /> },
    { label: 'Joyful ☀️', value: 'Joyful' },
    { label: 'Deep 💭', value: 'Reflective' },
    { label: 'Chill ☕', value: 'Chill' },
    { label: 'Vent 💨', value: 'Vent' },
];

const HomePage = ({ navigate }) => {
    const { currentUser, userProfile } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => {
        if (!db) return;
        const q = query(collection(db, "posts"));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
            const postsData = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(post => {
                    if (!post.createdAt) return true;
                    const time = post.createdAt?.toMillis ? post.createdAt.toMillis() : (post.createdAt?.seconds ? post.createdAt.seconds * 1000 : null);
                    return !time || time > twentyFourHoursAgo;
                })
                .sort((a, b) => {
                    const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
                    const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
                    return bTime - aTime;
                });
            setPosts(postsData);
            setLoading(false);
        }, (error) => {
            console.error("Posts query error:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const deletePost = async (postId) => {
        if (!currentUser) return;
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists() && postSnap.data().authorId === currentUser.uid) {
            await deleteDoc(postRef).catch(e => console.error("Error deleting post:", e));
        }
    };

    // Filter posts: remove blocked users, then apply active filter
    let visiblePosts = posts.filter(post => !userProfile?.blockedUsers?.includes(post.authorId));

    if (activeFilter === 'friends') {
        const friendsList = userProfile?.friends || [];
        visiblePosts = visiblePosts.filter(post => friendsList.includes(post.authorId) || post.authorId === currentUser?.uid);
    } else if (activeFilter !== 'all') {
        visiblePosts = visiblePosts.filter(post => post.mood === activeFilter);
    }

    return (
        <div className="space-y-3.5 sm:space-y-4 animate-fade-in">
            {/* Top Notes Carousel (Instagram-Style) */}
            <NotesCarousel navigate={navigate} />

            {/* Filter Pills */}
            <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar py-0.5 select-none">
                {MOOD_FILTERS.map(f => (
                    <button
                        key={f.value}
                        onClick={() => setActiveFilter(f.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all flex-shrink-0 ${
                            activeFilter === f.value
                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                                : 'bg-white dark:bg-[#111827]/80 border border-slate-200/80 dark:border-slate-800/80 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                    >
                        {f.icon && f.icon}
                        <span>{f.label}</span>
                    </button>
                ))}
            </div>

            {/* Feed Stream */}
            {loading ? (
                <div className="py-20 text-center space-y-3">
                    <div className="w-8 h-8 mx-auto rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                    <p className="text-xs text-slate-400 font-medium">Loading fleeting thoughts...</p>
                </div>
            ) : visiblePosts.length > 0 ? (
                <div className="space-y-3 sm:space-y-3.5">
                    {visiblePosts.map(post => (
                        <PostCard
                            key={post.id}
                            post={post}
                            navigate={navigate}
                            onDelete={deletePost}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-16 bg-white dark:bg-[#111827]/70 backdrop-blur-md border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-3">
                    <div className="w-12 h-12 bg-sky-50 dark:bg-slate-800/80 border border-sky-100 dark:border-slate-700/50 rounded-2xl flex items-center justify-center mx-auto text-xl shadow-xs">
                        💬
                    </div>

                    <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                        {activeFilter === 'friends' ? 'No thoughts from friends yet' : "Quiet for the moment"}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
                        {activeFilter === 'friends'
                            ? "Add friends from the Network tab or drop your own thought to get things moving."
                            : "Be the first to drop a 24-hour fleeting thought before the day ends."}
                    </p>
                    <button
                        onClick={() => navigate('create')}
                        className="px-5 py-2 bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white text-xs font-bold rounded-xl shadow-sm hover:scale-102 active:scale-98 transition-all"
                    >
                        Create a Post
                    </button>
                </div>
            )}
        </div>
    );
};

export default HomePage;